import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Rule,
	toGrammaticalNumber,
	TruncatedText,
} from "@hex-insights/core";
import { InternalLink, useActivePageInfo } from "@hex-insights/router";
import { Icon } from "../../../../../Components/Icon";
import { Tile } from "../../../../../Components/Tile";
import {
	MessageGroup,
	MessageGroupDetailQuery,
	truncateDisplayItems,
	useMessageGroupLatestTopicQuery,
	useMessageGroupUserGroupParticipantUserListQuery,
} from "../../../../../Utilities";
import { MessageGroupFormat, StudentFormat, UserFormat, UserGroupFormat } from "../../../../Format";
import { StudentLink, UserLink } from "../../../../Links";
import { MessageAvailabilityPeriodUtilities, MessageGroupUtilities } from "../../../../Utilities";
import * as MessageComponents from "../../../Message";
import * as MessageAvailabilityPeriodComponents from "../../../MessageAvailabilityPeriod";
import * as StudentComponents from "../../../Student";
import * as UserComponents from "../../../User";
import * as UserGroupComponents from "../../../UserGroup";
import { MessageGroupIcon } from "../../Icon";
import styles from "./styles.module.css";

export type HeaderProps = {
	messageGroupID: MessageGroup["id"];
	messageGroup: MessageGroupDetailQuery["messageGroup"] | undefined;
};

export function Header({ messageGroupID, messageGroup }: HeaderProps) {
	const users =
		messageGroup?.users.length === 1 && messageGroup.userGroups.length === 0
			? messageGroup.users
			: messageGroup?.users.filter((e) => !e.isCurrent) ?? []; // remove current user unless is only user
	const userGroups = messageGroup?.userGroups ?? [];
	const isSingleUser = users.length === 1 && userGroups.length === 0;

	const messageGroupName = messageGroup ? MessageGroupFormat.name(messageGroup) : "Loading...";

	const [hasTopic, setHasTopic] = React.useState(false);

	const isNarrow = !MessageGroupUtilities.useIsScreenWideForSideList();

	const { leafPage } = useActivePageInfo();

	return (
		<Tile
			className={makeClassName(
				styles["header"],
				hasTopic ? styles["header--with-topic"] : "",
				!messageGroup ? styles["header--loading"] : "",
			)}
		>
			<Tile.Body className={styles["header__body"]}>
				<Row justify="spaced-start" align="center" className={styles["header__content"]}>
					<If condition={isNarrow}>
						{!!leafPage.parent && (
							<InternalLink
								to={leafPage.parent.to}
								className={styles["header__back-link"] + " link--no-text-decoration"}
							>
								<Icon.CaretLeft size={24} className="block" />
							</InternalLink>
						)}
					</If>

					<Row justify="space-between" align="center" style={{ flexGrow: 1, minWidth: 0 }}>
						<Conditional>
							<If condition={!messageGroup}>Loading...</If>
							<If condition={isSingleUser}>
								{isSingleUser && <MessageGroupHeaderMembersIndividualUser name={messageGroupName} user={users[0]} />}
							</If>
							<Else>
								{messageGroup && (
									<MessageGroupHeaderMembersGeneral
										name={messageGroupName}
										messageGroup={messageGroup}
										users={users}
										isSingleUser={isSingleUser}
									/>
								)}
							</Else>
						</Conditional>

						<MessageGroupHeaderTopic messageGroupID={messageGroupID} setHasTopic={setHasTopic} />
					</Row>
				</Row>
			</Tile.Body>
		</Tile>
	);
}

type MessageGroupHeaderTopicProps = {
	messageGroupID: MessageGroup["id"];
	setHasTopic: (v: boolean) => void;
};

function MessageGroupHeaderTopic({ messageGroupID, setHasTopic }: MessageGroupHeaderTopicProps) {
	const { data } = useMessageGroupLatestTopicQuery({
		variables: { messageGroupID },
	});
	const topicMessage = data?.messageConnection.edges[0]?.node;
	const topicStudents = topicMessage?.messageTopic?.students ?? [];
	const { displayedItems: displayedTopicStudents, anyHidden, numHidden } = truncateDisplayItems(topicStudents, 3);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	const {
		isModalOpen: isMessageModalOpen,
		openModal: openMessageModal,
		closeModal: closeMessageModal,
	} = Modal.useToggle();

	const topicMessages = React.useMemo(() => (topicMessage ? [topicMessage] : []), [topicMessage]);

	const hasTopic = !!topicMessage && topicStudents.length > 0;

	React.useEffect(() => {
		setHasTopic(hasTopic);
	}, [setHasTopic, hasTopic]);

	if (!hasTopic) {
		return null;
	}

	let nameDisplay = "";
	if (topicStudents.length <= 2) {
		nameDisplay = englishCommaJoinStrings(topicStudents.map((e) => StudentFormat.name(e)));
	} else {
		nameDisplay = `${StudentFormat.name(topicStudents[0])} and ${topicStudents.length - 1} others`;
	}

	const iconSize = 60;

	return (
		<React.Fragment>
			<Row justify="spaced-start" align="center" className={styles["header__topic"]} style={{ minWidth: iconSize }}>
				<Column
					justify="spaced-start"
					align="flex-end"
					verticalSpacing="0.25rem"
					className={styles["header__topic__names-and-buttons"]}
					style={{ width: "100%", overflow: "hidden" }}
				>
					<Conditional>
						<If condition={topicStudents.length === 1}>
							<StudentLink instance={topicStudents[0]} className="link--no-text-decoration">
								<TruncatedText className={styles["header__topic__students__name"]}>{nameDisplay}</TruncatedText>
							</StudentLink>
						</If>
						<Else>
							<TruncatedText className={styles["header__topic__students__name"]}>{nameDisplay}</TruncatedText>
						</Else>
					</Conditional>
					<If condition={topicStudents.length > 1}>
						<Button variant="link" size="small" onClick={openModal} className="nowrap" style={{ padding: "0 0.4rem" }}>
							View All Students
						</Button>
					</If>
					<Button
						variant="link"
						size="small"
						onClick={openMessageModal}
						style={{ maxWidth: "100%", padding: "0 0.4rem", overflow: "hidden" }}
					>
						<TruncatedText style={{ width: "100%" }}>
							View <span className={styles["optional-text"]}>Original</span> Message
						</TruncatedText>
					</Button>
				</Column>

				<Button onClick={openModal} className={styles["header__topic__students__icons-button"]}>
					<Row
						justify="spaced-start"
						horizontalSpacing="0.5rem"
						className={styles["header__topic__students__icons--wide"]}
					>
						{displayedTopicStudents.map((student) => (
							<StudentComponents.StudentIcon key={student.id} student={student} size={iconSize} />
						))}
						<If condition={anyHidden}>
							<StudentComponents.StudentAdditionalCountIcon count={numHidden} size={iconSize} />
						</If>
					</Row>
					<div className={styles["header__members__icons--slim"]}>
						<StudentComponents.StudentMultiIcon students={topicStudents} size={iconSize} />
					</div>
				</Button>
			</Row>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal}>
					<Modal.Header>
						<Heading.H2 noMargin>Students</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<Column justify="spaced-start">
							{topicStudents.map((student) => (
								<StudentComponents.StudentIconNameDescription key={student.id} student={student} />
							))}
						</Column>
					</Modal.Body>
				</Modal>
			</Modal.If>

			<Modal.If condition={isMessageModalOpen}>
				<Modal onClose={closeMessageModal} style={{ height: "30rem", maxHeight: "90vh" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Original Message</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<MessageComponents.MessageSection author={topicMessage.author} messages={topicMessages} />
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type MessageGroupHeaderMembersIndividualUserProps = {
	name: string;
	user: MessageGroupDetailQuery["messageGroup"]["users"][0];
};

function MessageGroupHeaderMembersIndividualUser({ name, user }: MessageGroupHeaderMembersIndividualUserProps) {
	const { todaysAvailability, hasAvailabilitySet } = React.useMemo(() => {
		const todaysAvailability = MessageAvailabilityPeriodUtilities.getUserTodayAvailability(
			user.messageAvailabilityPeriods,
		);
		return { todaysAvailability, hasAvailabilitySet: user.messageAvailabilityPeriods.length > 0 };
	}, [user.messageAvailabilityPeriods]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Row justify="spaced-start" align="center" className={styles["header__members"]}>
			<UserComponents.UserWithMessagingStatusIcon key={user.id} user={user} size={60} />

			<Column justify="spaced-start" verticalSpacing="0.125rem" style={{ width: "100%", overflow: "hidden" }}>
				<UserLink instance={user} className="link--no-text-decoration">
					<TruncatedText className={styles["header__name"]}>{name}</TruncatedText>
				</UserLink>
				<TruncatedText className={styles["header__detail"]}>{UserFormat.role(user)}</TruncatedText>
				<If condition={hasAvailabilitySet}>
					<Row justify="spaced-start" align="center">
						<TruncatedText className={styles["header__detail"] + " " + styles["header__detail--availability"]}>
							{todaysAvailability}
						</TruncatedText>

						<Button variant="link" size="small" onClick={openModal} className="nowrap" style={{ padding: "0 0.4rem" }}>
							View Availability
						</Button>
					</Row>
				</If>
			</Column>

			<Modal.If condition={isModalOpen}>
				<MessageAvailabilityPeriodComponents.UserMessageAvailabilityModal user={user} onClose={closeModal} />
			</Modal.If>
		</Row>
	);
}

type MessageGroupHeaderMembersGeneralProps = {
	name: string;
	messageGroup: MessageGroupDetailQuery["messageGroup"];
	users: MessageGroupDetailQuery["messageGroup"]["users"];
	isSingleUser: boolean;
};

function MessageGroupHeaderMembersGeneral({
	name,
	messageGroup,
	users,
	isSingleUser,
}: MessageGroupHeaderMembersGeneralProps) {
	const members = MessageGroupUtilities.toMessageGroupMembers(users, messageGroup.userGroups);
	const { displayedItems: displayedMembers, anyHidden, numHidden } = truncateDisplayItems(members, 3);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Row justify="spaced-start" align="center" className={styles["header__members"]}>
			<Button onClick={openModal} className={styles["header__members__icons-button"]}>
				<Row justify="spaced-start" horizontalSpacing="0.5rem" className={styles["header__members__icons--wide"]}>
					{displayedMembers.map((member) => {
						if (member.user) {
							return <UserComponents.UserWithMessagingStatusIcon key={member.user.id} user={member.user} size={60} />;
						}
						return (
							<UserGroupComponents.UserGroupIcon key={member.userGroup.id} userGroup={member.userGroup} size={60} />
						);
					})}
					<If condition={anyHidden}>
						<UserComponents.UserAdditionalCountIcon count={numHidden} size={60} />
					</If>
				</Row>
				<div className={styles["header__members__icons--slim"]}>
					<MessageGroupIcon messageGroup={messageGroup} size={60} />
				</div>
			</Button>

			<Column
				justify="spaced-start"
				verticalSpacing="0.25rem"
				className={styles["header__members__names-and-buttons"]}
				style={{ width: "100%", overflow: "hidden" }}
			>
				<TruncatedText className={styles["header__name"]}>{name}</TruncatedText>
				<If condition={!isSingleUser}>
					<Button variant="link" size="small" onClick={openModal} className="nowrap">
						View All Participants
					</Button>
				</If>
			</Column>

			<Modal.If condition={isModalOpen}>
				<MessageGroupHeaderMessageParticipantsModal messageGroup={messageGroup} users={users} onClose={closeModal} />
			</Modal.If>
		</Row>
	);
}

type MessageGroupHeaderMessageParticipantsModalProps = {
	messageGroup: MessageGroupDetailQuery["messageGroup"];
	users: MessageGroupDetailQuery["messageGroup"]["users"];
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function MessageGroupHeaderMessageParticipantsModal({
	messageGroup,
	users,
	ifRef,
	onClose,
	className,
	style,
}: MessageGroupHeaderMessageParticipantsModalProps) {
	const { data } = useMessageGroupUserGroupParticipantUserListQuery({
		variables: {
			messageGroupID: messageGroup.id,
		},
	});
	const participantEdgesThroughGroups = data?.userConnection.edges ?? [];

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={className} style={{ width: "40rem", height: "35rem", ...style }}>
			<Modal.Header>
				<Heading.H2 noMargin>Group Chat Participants</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					{messageGroup?.userGroups.map((userGroup) => (
						<Row key={userGroup.id} justify="spaced-start" align="center">
							<UserGroupComponents.UserGroupIcon userGroup={userGroup} size={46} />

							<Column justify="spaced-start" verticalSpacing="0.25rem">
								<span className={styles["header__participants-modal__participant__name"]}>
									{UserGroupFormat.name(userGroup)}
								</span>
								<span className={styles["header__participants-modal__participant__description"]}>Group</span>
							</Column>
						</Row>
					))}
					{users.map((user) => (
						<MessageGroupHeaderMembersModalUser key={user.id} user={user} />
					))}

					<If condition={participantEdgesThroughGroups.length > 0}>
						<Rule thickness="2px" color="var(--neutral-colors--stroke)" />

						<Heading.H3 noMargin style={{ fontSize: "1rem" }}>
							From {toGrammaticalNumber("Group", messageGroup.userGroups.length)}
						</Heading.H3>

						{participantEdgesThroughGroups.map((e) => (
							<MessageGroupHeaderMembersModalUser key={e.node.id} user={e.node} />
						))}
					</If>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

type MessageGroupHeaderMembersModalUserProps = {
	user: MessageGroupDetailQuery["messageGroup"]["users"][0];
};

function MessageGroupHeaderMembersModalUser({ user }: MessageGroupHeaderMembersModalUserProps) {
	const { todaysAvailability, hasAvailabilitySet } = React.useMemo(() => {
		const todaysAvailability = MessageAvailabilityPeriodUtilities.getUserTodayAvailability(
			user.messageAvailabilityPeriods,
		);
		return { todaysAvailability, hasAvailabilitySet: user.messageAvailabilityPeriods.length > 0 };
	}, [user.messageAvailabilityPeriods]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Row justify="spaced-start" align="center">
			<UserComponents.UserWithMessagingStatusIcon user={user} size={46} />

			<Column justify="spaced-start" verticalSpacing="0.125rem">
				<UserLink
					instance={user}
					className={styles["header__participants-modal__participant__name"] + " link--no-text-decoration"}
				>
					{UserFormat.name(user)}
				</UserLink>
				<span className={styles["header__participants-modal__participant__description"]}>{UserFormat.role(user)}</span>
				<If condition={hasAvailabilitySet}>
					<Row justify="spaced-start" align="center">
						<span
							className={
								styles["header__participants-modal__participant__description"] +
								" " +
								styles["header__participants-modal__participant__description--availability"]
							}
						>
							{todaysAvailability}
						</span>

						<Button variant="link" size="small" onClick={openModal} style={{ padding: "0 0.4rem" }}>
							View Availability
						</Button>
					</Row>
				</If>
			</Column>

			<Modal.If condition={isModalOpen}>
				<MessageAvailabilityPeriodComponents.UserMessageAvailabilityModal user={user} onClose={closeModal} />
			</Modal.If>
		</Row>
	);
}
